/* eslint-disable multiline-comment-style */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import eBillAPI from 'services/eBill'
import { ServerError } from 'typings/serverError'
import {
  GetStatementDetailsRequest,
  StatementListRequest
} from 'services/eBill/typings'
import { AnyObject } from 'typings/custom'
import { GridOption } from 'typings/grid'
import { StatementSummary } from 'typings/statement'

export interface StatementState {
  isParentDataLoading?: boolean
  isChildDataLoading?: boolean
  dataList?: Array<StatementSummary>
  childDataList?: Array<StatementSummary>
  isResponseFail?: boolean
  gridOption?: GridOption
  filterOption?: AnyObject
  childAccounts?: Array<AnyObject>
  statementDetails?: string
  isStatementDetailsLoading?: boolean
  isStatementDetailsError?: boolean
  errorMessage?: string
  isEbillEnrollmentUpdated?: boolean
  isEbillProfileUpdated?: boolean
  error?: boolean
  isEnrollmentDone?: boolean
}

export const initState: StatementState = {
  isParentDataLoading: true,
  isChildDataLoading: true,
  error: false,
  dataList: [],
  childDataList: [],
  isResponseFail: false,
  errorMessage: '',
  gridOption: {
    limit: 20,
    currentPage: 1
  },
  filterOption: {},
  childAccounts: [],
  statementDetails: '',
  isStatementDetailsLoading: false,
  isStatementDetailsError: false,
  isEbillEnrollmentUpdated: false,
  isEbillProfileUpdated: false,
  isEnrollmentDone: false
}

const sliceName = 'statements'

export const getStatementList = createAsyncThunk(
  `${sliceName}/getStatementList`,
  async (payload: StatementListRequest, { rejectWithValue }) => {
    try {
      const { isChildAccount, accountUniqueIdentifiers } = payload
      const response = await eBillAPI.getStatementList(payload)
      return { isChildAccount, accountUniqueIdentifiers, ...response }
    } catch (error) {
      return rejectWithValue(error as ServerError)
    }
  }
)

export const getStatementDetails = createAsyncThunk(
  `${sliceName}/getStatementDetails`,
  async (payload: GetStatementDetailsRequest) => {
    return eBillAPI.getStatementDetails(payload)
  }
)

export const statementSlice = createSlice({
  name: sliceName,
  initialState: initState,
  reducers: {
    updateStatementState(
      state,
      action: PayloadAction<Partial<StatementState>>
    ) {
      return { ...state, ...action.payload }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getStatementList.pending, (state, action) => {
        const isChildAccount = action?.meta?.arg?.isChildAccount
        state.isParentDataLoading = !isChildAccount
        state.isChildDataLoading = isChildAccount
      })
      .addCase(getStatementList.fulfilled, (state, action) => {
        const { isChildAccount, summary, accountUniqueIdentifiers } =
          action.payload
        const key = isChildAccount ? accountUniqueIdentifiers[0] : 'parent'
        const dataList = summary?.[key]?.map(item => {
          const { pdfLocation } = item
          const statementID = (pdfLocation.split('/') || [])[7]
          return {
            ...item,
            statementID
          }
        })
        if (isChildAccount) {
          state.childDataList = dataList
        } else {
          state.dataList = dataList
        }
        state.isResponseFail = false
        state.isChildDataLoading = false
        state.isParentDataLoading = false
        state.errorMessage = ''
      })
      .addCase(getStatementList.rejected, (state, action) => {
        state.isParentDataLoading = false
        state.isChildDataLoading = false
        state.dataList = []
        state.childDataList = []
        state.isResponseFail = true
        state.errorMessage =
          (action.payload as ServerError).response?.message ||
          'Unexpected error'
      })
      .addCase(getStatementDetails.pending, state => {
        state.isStatementDetailsLoading = true
        state.isStatementDetailsError = false
        state.errorMessage = ''
        state.statementDetails = ''
      })
      .addCase(getStatementDetails.fulfilled, (state, action) => {
        state.statementDetails = action.payload.pdfContent
        state.isStatementDetailsLoading = false
        state.isStatementDetailsError = false
        state.errorMessage = ''
      })
      .addCase(getStatementDetails.rejected, (state, action) => {
        state.isStatementDetailsLoading = false
        state.statementDetails = ''
        state.isStatementDetailsError = true
        state.errorMessage =
          (action.payload as ServerError).response?.message ||
          'Unexpected error'
      })
  }
})

export default statementSlice.reducer
export const { updateStatementState } = statementSlice.actions
